// ============================================================
// report-download.ts  — 帳票ダウンロード共通処理
// ============================================================
//
// 帳票の DL ボタン (<a class="report-download" href="...">) を
// クリックしたとき、別タブを開かずに現在のタブのまま fetch +
// Blob でダウンロードを実行する。
//
// ただし iOS / iPadOS の WebKit は blob URL に download 属性が効かず
// タブ内でファイルを開いてしまうため、そこだけは非表示 iframe に
// attachment レスポンスを読ませて Safari のダウンロード機能に渡す。
//
// ローディングは画面共通の表示 (loading.ts) を使い、帳票生成中である
// ことを文言で伝える。
//
// ダウンロード中は AutoReload を一時停止して、自動更新と
// 帳票生成のサーバー負荷の取り合いを避ける。

import { hideLoading, showLoading } from './loading';

(function () {

    function decodeRfc5987(value: string): string {
        // filename*=UTF-8''xxxx 形式
        const m = /^([^']*)'([^']*)'(.+)$/.exec(value);
        if (!m) {
            return value;
        }
        try {
            return decodeURIComponent(m[3]);
        } catch {
            return m[3];
        }
    }

    function parseFilename(header: string | null): string | null {
        if (!header) {
            return null;
        }

        // RFC 5987 拡張 (filename*=UTF-8''...) を優先
        const star = /filename\*\s*=\s*([^;]+)/i.exec(header);
        if (star) {
            return decodeRfc5987(star[1].trim());
        }

        const plain = /filename\s*=\s*"?([^";]+)"?/i.exec(header);
        if (plain) {
            return plain[1].trim();
        }

        return null;
    }

    function fallbackFilename(url: string, contentType: string | null): string {
        try {
            const u = new URL(url, window.location.origin);
            const last = u.pathname.split('/').filter((s) => s).pop() ?? 'download';
            const ft = u.searchParams.get('FileType');
            if (ft) {
                return `${last}.${ft}`;
            }
            if (contentType) {
                if (contentType.includes('pdf')) return `${last}.pdf`;
                if (contentType.includes('spreadsheetml')) return `${last}.xlsx`;
                if (contentType.includes('csv')) return `${last}.csv`;
            }
            return last;
        } catch {
            return 'download';
        }
    }

    function showOverlay(): void {
        showLoading('帳票をダウンロード中...', '完了までしばらくお待ちください');
    }

    function hideOverlay(): void {
        hideLoading();
    }

    // iOS / iPadOS の WebKit は blob URL に対して download 属性を無視し、
    // タブ内でファイルを開いてしまう (画面遷移して見える)。
    function isIosWebKit(): boolean {
        const ua = navigator.userAgent;
        if (/iPad|iPhone|iPod/.test(ua)) {
            return true;
        }
        // iPadOS 13 以降は Macintosh を名乗るため、タッチ対応で判定する
        return /Macintosh/.test(ua) && navigator.maxTouchPoints > 1;
    }

    // サーバーは Content-Disposition: attachment を返すため、非表示 iframe に
    // 読み込ませるとページを離れずに Safari のダウンロード機能へ渡せる。
    function downloadViaFrame(href: string): void {
        showOverlay();

        const frame = document.createElement('iframe');
        frame.hidden = true;
        frame.setAttribute('aria-hidden', 'true');
        frame.title = '帳票ダウンロード';

        let finished = false;
        const finish = () => {
            if (finished) {
                return;
            }
            finished = true;
            hideOverlay();
            // ダウンロード開始後に取り除く。即座に消すと転送が中断されることがある。
            window.setTimeout(() => frame.remove(), 60000);
        };

        // 添付ファイルでは load が発火しない環境もあるため、時間でも解除する。
        frame.addEventListener('load', finish);
        window.setTimeout(finish, 3000);

        frame.src = href;
        document.body.appendChild(frame);
    }

    function saveBlob(blob: Blob, filename: string): void {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        // Revoke は遅延実行（ブラウザが DL を開始するのを待つ）
        setTimeout(() => URL.revokeObjectURL(url), 1000);
    }

    function showError(message: string): void {
        const bb = (window as any).bootbox;
        if (bb && typeof bb.alert === 'function') {
            bb.alert(message);
        } else {
            window.alert(message);
        }
    }

    async function download(href: string): Promise<void> {
        showOverlay();
        try {
            const res = await fetch(href, {
                method: 'GET',
                credentials: 'same-origin',
                headers: { 'X-Requested-With': 'XMLHttpRequest' },
            });

            if (!res.ok) {
                throw new Error(`HTTP ${res.status} ${res.statusText}`);
            }

            const blob = await res.blob();
            const contentType = res.headers.get('Content-Type');
            const filename =
                parseFilename(res.headers.get('Content-Disposition')) ??
                fallbackFilename(href, contentType);

            saveBlob(blob, filename);
        } catch (e) {
            const msg = e instanceof Error ? e.message : String(e);
            showError(`ダウンロードに失敗しました: ${msg}`);
        } finally {
            hideOverlay();
        }
    }

    document.addEventListener('click', function (event) {
        // 修飾キー付き (Ctrl/Cmd/Shift/Middle click) の場合は
        // ブラウザのデフォルト挙動 (別タブで開く等) を尊重する
        const me = event as MouseEvent;
        if (me.button !== 0 || me.ctrlKey || me.metaKey || me.shiftKey || me.altKey) {
            return;
        }

        const target = event.target as HTMLElement | null;
        if (!target) {
            return;
        }

        const link = target.closest('a.report-download') as HTMLAnchorElement | null;
        if (!link) {
            return;
        }

        const href = link.getAttribute('href');
        if (!href || href === '#') {
            return;
        }

        event.preventDefault();
        // ドロップダウン内から起動された場合に開いたままにならないよう
        // フォーカスを外して Bootstrap dropdown を閉じる
        link.blur();

        if (isIosWebKit()) {
            downloadViaFrame(href);
            return;
        }

        void download(href);
    });
})();
